import React, { FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useResetPasswordMutation } from "../../store/api/authApi";
import "./AuthPage.css";

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (newPassword !== confirm) { setError("Passwords do not match."); return; }
    try {
      const res = await resetPassword({ token, newPassword }).unwrap();
      if (res.success) { setDone(true); setTimeout(() => navigate("/admin"), 3000); }
      else setError(res.message || "Reset failed. The link may have expired.");
    } catch {
      setError("Invalid or expired reset link. Please request a new one.");
    }
  };

  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-brand">
            <div className="auth-brand-icon"><i className="bi bi-shield-lock-fill" /></div>
            <div>
              <div className="auth-brand-name">Dialin24</div>
              <div className="auth-brand-sub">Admin Portal</div>
            </div>
          </div>
          <div className="auth-error-block">
            <i className="bi bi-exclamation-triangle-fill" />
            <h2 className="auth-title mt-3">Invalid link</h2>
            <p className="auth-copy">This password reset link is invalid or missing a token.</p>
            <Link to="/forgot-password" className="btn auth-submit-btn w-100 mt-2">
              Request a new link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        {/* Brand */}
        <div className="auth-brand">
          <div className="auth-brand-icon"><i className="bi bi-shield-lock-fill" /></div>
          <div>
            <div className="auth-brand-name">Dialin24</div>
            <div className="auth-brand-sub">Admin Portal</div>
          </div>
        </div>

        {done ? (
          /* ── Success state ── */
          <div className="auth-success">
            <div className="auth-success-icon auth-success-icon--green">
              <i className="bi bi-check-circle-fill" />
            </div>
            <h2 className="auth-title">Password updated!</h2>
            <p className="auth-copy">
              Your password has been reset successfully. Redirecting you to
              login…
            </p>
            <Link to="/admin" className="btn auth-submit-btn w-100 mt-3">
              Go to Login
            </Link>
          </div>
        ) : (
          /* ── Form state ── */
          <>
            <h2 className="auth-title">Set a new password</h2>
            <p className="auth-copy">
              Choose a strong password with at least 8 characters.
            </p>

            <form onSubmit={handleSubmit} noValidate>
              {/* New password */}
              <div className="auth-field">
                <label htmlFor="rpNew" className="auth-label">New password</label>
                <div className="auth-input-wrap">
                  <i className="bi bi-lock auth-input-icon" />
                  <input
                    id="rpNew"
                    type={showNew ? "text" : "password"}
                    className="auth-input auth-input-pr"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  <button type="button" className="auth-eye-btn" tabIndex={-1}
                    onClick={() => setShowNew((v) => !v)}
                    aria-label={showNew ? "Hide" : "Show"}>
                    <i className={`bi ${showNew ? "bi-eye-slash" : "bi-eye"}`} />
                  </button>
                </div>
              </div>

              {/* Confirm password */}
              <div className="auth-field">
                <label htmlFor="rpConfirm" className="auth-label">Confirm password</label>
                <div className="auth-input-wrap">
                  <i className="bi bi-lock-fill auth-input-icon" />
                  <input
                    id="rpConfirm"
                    type={showConfirm ? "text" : "password"}
                    className="auth-input auth-input-pr"
                    placeholder="••••••••"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  <button type="button" className="auth-eye-btn" tabIndex={-1}
                    onClick={() => setShowConfirm((v) => !v)}
                    aria-label={showConfirm ? "Hide" : "Show"}>
                    <i className={`bi ${showConfirm ? "bi-eye-slash" : "bi-eye"}`} />
                  </button>
                </div>
              </div>

              {/* Strength hint */}
              {newPassword.length > 0 && (
                <div className={`auth-strength ${newPassword.length >= 8 ? "auth-strength--ok" : "auth-strength--weak"}`}>
                  <i className={`bi ${newPassword.length >= 8 ? "bi-check-circle" : "bi-exclamation-circle"}`} />
                  {newPassword.length >= 8 ? " Strong enough" : " At least 8 characters required"}
                </div>
              )}

              {error && <div className="auth-error">{error}</div>}

              <button type="submit" className="btn auth-submit-btn w-100" disabled={isLoading}>
                {isLoading ? (
                  <><span className="spinner-border spinner-border-sm me-2" role="status" />Updating…</>
                ) : (
                  <><i className="bi bi-check2-circle me-2" />Update Password</>
                )}
              </button>
            </form>

            <p className="auth-footer-link">
              <Link to="/forgot-password">
                <i className="bi bi-arrow-left me-1" />
                Request a different link
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
